import * as Sentry from '@sentry/react';

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('Sentry DSN not configured. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Release tracking
    release: import.meta.env.VITE_SENTRY_RELEASE || `calie@${import.meta.env.VITE_GIT_SHA || 'dev'}`,
    // Enable debug mode in development
    debug: import.meta.env.DEV,
    // Error filtering
    beforeSend(event, hint) {
      // Filter out errors from browser extensions
      if (event.exception?.values?.[0]?.stacktrace?.frames?.some(
        frame => frame.filename?.includes('chrome-extension://') || frame.filename?.includes('moz-extension://')
      )) {
        return null;
      }
      return event;
    },
  });
}

export function setSentryUser(user: { id: string; email?: string; tenantId?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    tenantId: user.tenantId,
  });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

export function captureSentryError(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.setContext('additional', context);
  }
  Sentry.captureException(error);
}

export function addSentryBreadcrumb(message: string, category: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

export { Sentry };
